import * as React from 'react';
import { useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTheme } from '@mui/material/styles';
import { getAllSessions } from '../api/sessions';
import dayjs from 'dayjs';

export default function PageViewsBarChart({ selectedDate, barberName }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState({
    services: [],
    extras: [],
    items: [],
    dates: [],
    total: 0,
  });

  const colorPalette = [
    (theme.vars || theme).palette.primary.dark,
    (theme.vars || theme).palette.primary.main,
    (theme.vars || theme).palette.primary.light,
  ];

  // Calculate date ranges
  const currentWeekEnd = new Date(selectedDate);
  currentWeekEnd.setHours(23, 59, 59, 999);
  const currentWeekStart = new Date(selectedDate);
  currentWeekStart.setDate(currentWeekEnd.getDate() - 6);
  currentWeekStart.setHours(0, 0, 0, 0);
  const previousWeekEnd = new Date(currentWeekStart);
  previousWeekEnd.setDate(currentWeekStart.getDate() - 1);
  previousWeekEnd.setHours(23, 59, 59, 999);
  const previousWeekStart = new Date(previousWeekEnd);
  previousWeekStart.setDate(previousWeekEnd.getDate() - 6);
  previousWeekStart.setHours(0, 0, 0, 0);

  // Fetch data with barber_name filter
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);
        const data = await getAllSessions();
        
        // Filter sessions by barber_name
        const filteredData = data.filter(session => session.barber_name === barberName);

        // Process data for 14 days (current + previous week)
        const dateRange = [];
        const servicesData = [];
        const extrasData = [];
        const itemsData = [];
        let totalSales = 0;

        // Generate 14-day range starting from previousWeekStart
        let currentDate = new Date(previousWeekStart);
        for (let i = 0; i < 14; i++) {
          dateRange.push(dayjs(currentDate).format('MMM D'));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Initialize arrays with zeros
        servicesData.length = 14;
        extrasData.length = 14;
        itemsData.length = 14;
        servicesData.fill(0);
        extrasData.fill(0);
        itemsData.fill(0);

        // Process filtered session data
        filteredData.forEach((session) => {
          const sessionDate = new Date(session.date);
          if (sessionDate >= previousWeekStart && sessionDate <= currentWeekEnd) {
            const dayIndex = Math.floor((sessionDate - previousWeekStart) / (1000 * 60 * 60 * 24));
            if (dayIndex >= 0 && dayIndex < 14) {
              // Sum up prices correctly for services, extras, and items
              const servicePrice = Array.isArray(session.services)
                ? session.services.reduce((sum, service) => sum + (service.price || 0), 0)
                : 0;
                
              const extraPrice = Array.isArray(session.extras)
                ? session.extras.reduce((sum, extra) => sum + (extra.price || 0), 0)
                : 0;
                
              const itemPrice = Array.isArray(session.items)
                ? session.items.reduce((sum, item) => sum + (item.price || 0), 0)
                : 0;
        
              servicesData[dayIndex] += servicePrice;
              extrasData[dayIndex] += extraPrice;
              itemsData[dayIndex] += itemPrice;
              totalSales += servicePrice + extraPrice + itemPrice;
            }
          }
        });
        

        setSalesData({
          services: servicesData,
          extras: extrasData,
          items: itemsData,
          dates: dateRange,
          total: totalSales,
        });
      } catch (error) {
        console.error('Error fetching sales data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [selectedDate, barberName]); // Added barber_name to dependency array

  // Calculate percentage change from previous week
  const currentWeekTotal = salesData.services.slice(7).reduce((a, b) => a + b, 0) +
                          salesData.extras.slice(7).reduce((a, b) => a + b, 0) +
                          salesData.items.slice(7).reduce((a, b) => a + b, 0);
  const previousWeekTotal = salesData.services.slice(0, 7).reduce((a, b) => a + b, 0) +
                           salesData.extras.slice(0, 7).reduce((a, b) => a + b, 0) +
                           salesData.items.slice(0, 7).reduce((a, b) => a + b, 0);
  const percentageChange = previousWeekTotal === 0 
    ? (currentWeekTotal > 0 ? 100 : 0)
    : ((currentWeekTotal - previousWeekTotal) / previousWeekTotal * 100).toFixed(1);

  return (
    <Card variant="outlined" sx={{ width: '100%'}}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {barberName}'s Sales Overview
        </Typography>
        <Stack sx={{ justifyContent: 'space-between' }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: 'center', sm: 'flex-start' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h4" component="p">
              €{salesData.total.toFixed(2)}
            </Typography>
            <Chip 
              size="small" 
              color={percentageChange >= 0 ? "success" : "error"} 
              label={`${percentageChange >= 0 ? '+' : ''}${percentageChange}%`}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Sales breakdown for the last 14 days
          </Typography>
        </Stack>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <BarChart
            borderRadius={8}
            colors={colorPalette}
            xAxis={[
              {
                scaleType: 'band',
                categoryGapRatio: 0.5,
                data: salesData.dates,
              },
            ]}
            series={[
              {
                id: 'services',
                label: 'Services',
                data: salesData.services,
                stack: 'A',
              },
              {
                id: 'extras',
                label: 'Extras',
                data: salesData.extras,
                stack: 'A',
              },
              {
                id: 'items',
                label: 'Items',
                data: salesData.items,
                stack: 'A',
              },
            ]}
            height={250}
            margin={{ left: 50, right: 0, top: 20, bottom: 20 }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: {
                hidden: false,
                position: { vertical: 'top', horizontal: 'right' },
                padding: 0,
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}